import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered } from 'lucide-react';
import { useEffect } from 'react';
import { cn } from '@/lib/utils';

const ToolbarButton = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={cn(
      'p-1.5 rounded transition-colors',
      'text-[#0f172a] dark:text-[#eef2ff]',
      'hover:bg-slate-200 dark:hover:bg-[#1a2030]',
      'disabled:opacity-40 disabled:cursor-not-allowed',
      active && 'bg-slate-300 dark:bg-[#1e2638]'
    )}
  >
    {children}
  </button>
);

export function RichTextField({ value, onChange, placeholder, id, name }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: placeholder || '' }),
      Markdown.configure({ html: false, transformCopiedText: true }),
    ],
    content: value || '',
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const md = editor.storage.markdown.getMarkdown();
      onChange(md);
    },
    editorProps: {
      attributes: {
        id: id || '',
        name: name || '',
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[120px] px-3 py-2 text-[#0f172a] dark:text-[#eef2ff] dark:prose-invert',
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.storage.markdown.getMarkdown();
    if (current !== value) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const charCount = editor?.storage.markdown.getMarkdown().length || 0;

  if (!editor) return null;

  return (
    <div className="w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] box-border focus-within:ring-2 focus-within:ring-[#5b8cff]">
      <div className="flex items-center gap-1 border-b border-black/10 dark:border-white/[0.08] px-2 py-1.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Fett (Strg+B)"
        >
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Kursiv (Strg+I)"
        >
          <Italic size={14} />
        </ToolbarButton>
        <div className="w-px h-4 bg-black/10 dark:bg-white/[0.08] mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
          title="Überschrift 2"
        >
          <Heading2 size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive('heading', { level: 3 })}
          title="Überschrift 3"
        >
          <Heading3 size={14} />
        </ToolbarButton>
        <div className="w-px h-4 bg-black/10 dark:bg-white/[0.08] mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Aufzählung"
        >
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Nummerierte Liste"
        >
          <ListOrdered size={14} />
        </ToolbarButton>
      </div>

      <EditorContent editor={editor} />

      <div className="px-3 py-1 text-[11px] text-slate-500 dark:text-[#93a4bf] border-t border-black/10 dark:border-white/[0.08]">
        ca. {charCount} Zeichen
      </div>
    </div>
  );
}

export default RichTextField;
